/**
 * Times are stored as integer seconds everywhere and formatted only at the
 * render edge. SPEC.md §4. This is the first file in the build order because
 * everything that touches a clock depends on it.
 */

/**
 * Formats seconds as `h:mm:ss` (or `mm:ss` under an hour).
 * Returns an em dash for null so callers never have to branch.
 */
export function formatTime(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return '—';
  const s = Math.round(seconds);
  if (s < 0) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(rest)}` : `${m}:${pad(rest)}`;
}

/**
 * Parses `h:mm:ss` or `mm:ss` into seconds.
 * Returns null on anything it can't read, so the caller shows a validation error.
 */
export function parseTime(input) {
  if (!input) return null;
  const parts = input.trim().split(':');
  if (parts.length !== 2 && parts.length !== 3) return null;
  const nums = parts.map((p) => Number(p.trim()));
  if (nums.some((n) => !Number.isFinite(n) || n < 0 || !Number.isInteger(n))) return null;
  if (parts.length === 3) {
    const [h, m, s] = nums;
    if (m > 59 || s > 59) return null;
    return h * 3600 + m * 60 + s;
  }
  const [m, s] = nums;
  if (s > 59) return null;
  return m * 60 + s;
}

/** Pace per km, formatted `m:ss/km`. */
export function formatPace(seconds, km) {
  if (seconds == null || !km) return '—';
  return `${formatTime(Math.round(seconds / km))}/km`;
}

/* ---------- dates: ISO `YYYY-MM-DD` strings everywhere ---------- */

/** Today as an ISO date string, in the browser's local timezone. */
export function today() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Parses an ISO date at local midnight, avoiding UTC drift. */
export function parseISO(iso) {
  return new Date(`${iso}T00:00:00`);
}

/** Formats an ISO date as `12 Apr 26`. */
export function formatDate(iso) {
  if (!iso) return '—';
  return parseISO(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });
}

/** Formats an ISO date as `Sunday, 10 August 2026`. */
export function formatLongDate(iso) {
  return parseISO(iso).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

/** `{ dd: '10', mm: 'Aug' }` — the date block on an event card. */
export function dateBlock(iso) {
  const d = parseISO(iso);
  return {
    dd: String(d.getDate()).padStart(2, '0'),
    mm: d.toLocaleDateString('en-GB', { month: 'short' }),
  };
}

/** Adds calendar months to an ISO date, clamping to end-of-month. */
export function addMonths(iso, months) {
  const d = parseISO(iso);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Whole days from today to an ISO date. Negative means it has passed. */
export function daysTo(iso) {
  return Math.round((parseISO(iso).getTime() - parseISO(today()).getTime()) / 86_400_000);
}

/** Age in whole years from a date of birth. */
export function ageFrom(dob) {
  if (!dob) return null;
  const d = parseISO(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  if (
    now.getMonth() < d.getMonth() ||
    (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())
  ) {
    age--;
  }
  return age;
}

/** Indian financial year for a date — April to March. `2026-04-01` → `2026-27`. */
export function financialYear(iso) {
  const d = parseISO(iso);
  const y = d.getFullYear();
  return d.getMonth() >= 3
    ? `${y}-${String(y + 1).slice(2)}`
    : `${y - 1}-${String(y).slice(2)}`;
}

/** Relative time for chat and message lists. */
export function timeAgo(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86_400)}d ago`;
}

/**
 * An ISO date `n` days from an ISO date. Negative goes backwards.
 *
 * Built on the same parse-and-reformat discipline as addMonths: constructed
 * from local date parts, never from Date arithmetic on a UTC string, because
 * an IST user near midnight would otherwise land on the previous day.
 */
export function addDays(iso, n) {
  if (!iso) return null;
  const d = parseISO(iso);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + n);
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
